/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useEffect, useState } from 'react';

import { css, jsx } from '@compiled/react';

import { extractInstruction } from '@atlaskit/pragmatic-drag-and-drop-hitbox/tree-item';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { token } from '@atlaskit/tokens';

import type { LinksMenuExtensionActions } from '../../../../controllers/use-links-menu-parameters-and-actions';
import type { ItemPositionalProps, Items, ItemType, MenuItemType } from '../../../../types';

import {
	DraggableLinksMenuItemContainer,
	type DraggableLinksMenuItemData,
} from './draggable-links-menu-item-container';
import { LinksMenuItem } from './links-menu-item';

const treeStyles = css({
	paddingLeft: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles
	ul: {
		// Have to share the per-level index px with config popup
		marginLeft: token('space.200'),
	},
});

type LinksMenuItemTreeProps = {
	extensionActions: LinksMenuExtensionActions;
	level?: number;
	nodes: Items;
	numberItems: number;
	parentId?: string;
};

export const LinksMenuItemTree: React.FC<LinksMenuItemTreeProps> = ({
	extensionActions,
	nodes,
	parentId,
	level = 0,
	numberItems,
}) => {
	const { moveItem } = extensionActions;

	useEffect(() => {
		return monitorForElements({
			onDrop({ source, location }) {
				const destination = location.current.dropTargets[0];
				if (destination?.data && source?.data) {
					const sourceData = source.data as DraggableLinksMenuItemData;
					const destinationData = destination.data as DraggableLinksMenuItemData;
					const instruction = extractInstruction(destinationData);

					// source and destination should be unique
					if (sourceData.id !== destinationData.id) {
						if (instruction?.type === 'reorder-above' || instruction?.type === 'reorder-below') {
							// if source and destination have same parent, then we are reordering within the list
							// and the index depends on whether the source is above or below the destination
							const index = destinationData.index || 0;

							let newIndex = instruction?.type === 'reorder-above' ? index : index + 1;
							if (sourceData.parentId === destinationData.parentId) {
								if (sourceData.index < index) {
									newIndex = instruction?.type === 'reorder-above' ? index - 1 : index;
								}
							}
							moveItem(sourceData.item, newIndex, destinationData.parentId);
						}
						if (instruction?.type === 'make-child') {
							moveItem(sourceData.item, 0, destinationData.id);
						}
					}
				}
			},
		});
	}, [nodes, moveItem]);

	return (
		// Legacy .wiki-content class applies list styling that
		// can only be overridden with high specificity or inline
		// style application
		// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
		<ul css={treeStyles} style={{ listStyleType: 'none' }}>
			{nodes.map((node, index) => (
				<LinksMenuItemTreeNode
					key={node.itemId}
					extensionActions={extensionActions}
					node={node}
					isFirst={index === 0}
					isLast={index === nodes.length - 1}
					index={index}
					parentId={parentId}
					level={level}
					numberItems={numberItems}
				/>
			))}
		</ul>
	);
};

type TreeNodeProps = ItemPositionalProps & {
	extensionActions: LinksMenuExtensionActions;
	node: ItemType;
	numberItems: number;
	parentId?: string;
};

const LinksMenuItemTreeNode: React.FC<TreeNodeProps> = ({
	extensionActions,
	node,
	isFirst,
	isLast,
	index,
	parentId,
	level,
	numberItems,
}) => {
	const [isDragging, setIsDragging] = useState<boolean>(false);
	const newLevel = level + 1;

	return (
		<li>
			<DraggableLinksMenuItemContainer
				item={node}
				index={index}
				parentId={parentId}
				isDragging={isDragging}
				setIsDragging={setIsDragging}
				level={level}
			>
				<LinksMenuItem
					key={node.itemId}
					extensionActions={extensionActions}
					index={index}
					isFirst={isFirst}
					isLast={isLast}
					item={node}
					parentId={parentId}
					level={level}
					numberItems={numberItems}
					isDragging={isDragging}
				/>
			</DraggableLinksMenuItemContainer>
			{(node as MenuItemType).childItems && !isDragging && (
				<LinksMenuItemTree
					extensionActions={extensionActions}
					nodes={(node as MenuItemType).childItems!}
					parentId={node.itemId}
					level={newLevel}
					numberItems={numberItems}
				/>
			)}
		</li>
	);
};
